import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Modal, Form, InputNumber, Select, Slider, Space,
  Typography, Tag, Row, Col, Spin, message, Tooltip, Empty, Statistic, Input
} from 'antd';
import {
  TrophyOutlined, RiseOutlined, FallOutlined, MinusOutlined,
  PlusOutlined, SettingOutlined, ReloadOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { scorecardService, clientVendorService } from '../services/api';

const { Title, Text } = Typography;

const VendorScorecards = () => {
  const [rankings, setRankings] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [weights, setWeights] = useState({
    quality_weight: 25,
    delivery_weight: 25,
    cost_weight: 25,
    responsiveness_weight: 25
  });
  const [loading, setLoading] = useState(true);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [scoreModalVisible, setScoreModalVisible] = useState(false);
  const [weightsModalVisible, setWeightsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [weightsForm] = Form.useForm();

  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);

  useEffect(() => {
    loadData();
  }, [selectedYear, selectedMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rankingsData, vendorsData, weightsData] = await Promise.all([
        scorecardService.getRankings({ year: selectedYear, month: selectedMonth }),
        clientVendorService.getAll({ status: 'active' }),
        scorecardService.getWeights()
      ]);
      setRankings(rankingsData);
      setVendors(vendorsData);
      setWeights(weightsData);

      // Select first vendor if available
      if (rankingsData.length > 0 && !selectedVendor) {
        handleVendorSelect(rankingsData[0]);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      message.error('Failed to load scorecard data');
    } finally {
      setLoading(false);
    }
  };

  const handleVendorSelect = async (vendor) => {
    setSelectedVendor(vendor);
    try {
      const trend = await scorecardService.getVendorTrend(vendor.client_vendor_id, 12);
      setTrendData(trend);
    } catch (error) {
      console.error('Failed to load trend:', error);
      setTrendData([]);
    }
  };

  const handleCreateScore = () => {
    form.resetFields();
    form.setFieldsValue({
      year: selectedYear,
      month: selectedMonth,
      quality_score: 75,
      delivery_score: 75,
      cost_score: 75,
      responsiveness_score: 75
    });
    setScoreModalVisible(true);
  };

  const handleSaveScore = async () => {
    try {
      const values = await form.validateFields();
      await scorecardService.create(values);
      message.success('Scorecard saved successfully');
      setScoreModalVisible(false);
      loadData();
    } catch (error) {
      console.error('Failed to save scorecard:', error);
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('Failed to save scorecard');
      }
    }
  };

  const handleSaveWeights = async () => {
    try {
      const values = await weightsForm.validateFields();
      await scorecardService.updateWeights(values);
      message.success('Weights updated successfully');
      setWeightsModalVisible(false);
      setWeights(values);
      loadData();
    } catch (error) {
      console.error('Failed to save weights:', error);
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('Failed to save weights');
      }
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <RiseOutlined style={{ color: '#52c41a' }} />;
      case 'down':
        return <FallOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <MinusOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  const getRiskColor = (score) => {
    if (score >= 50) return '#ff4d4f';
    if (score >= 25) return '#faad14';
    return '#52c41a';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#1890ff';
    if (score >= 40) return '#faad14';
    return '#ff4d4f';
  };

  const columns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 60,
      render: (rank) => (
        <Space>
          {rank <= 3 && <TrophyOutlined style={{ color: rank === 1 ? '#ffd700' : rank === 2 ? '#c0c0c0' : '#cd7f32' }} />}
          <Text strong>#{rank}</Text>
        </Space>
      ),
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      render: (name, record) => (
        <Button type="link" onClick={() => handleVendorSelect(record)} style={{ padding: 0 }}>
          {name}
        </Button>
      ),
    },
    {
      title: 'Overall',
      dataIndex: 'overall_score',
      key: 'overall_score',
      width: 100,
      sorter: (a, b) => a.overall_score - b.overall_score,
      render: (score) => (
        <Tag color={getScoreColor(score)} style={{ fontSize: 14, padding: '2px 8px' }}>
          {score.toFixed(0)}
        </Tag>
      ),
    },
    {
      title: 'Quality',
      dataIndex: 'quality_score',
      key: 'quality_score',
      width: 80,
      render: (score) => <Text style={{ color: getScoreColor(score) }}>{score.toFixed(0)}</Text>,
    },
    {
      title: 'Delivery',
      dataIndex: 'delivery_score',
      key: 'delivery_score',
      width: 80,
      render: (score) => <Text style={{ color: getScoreColor(score) }}>{score.toFixed(0)}</Text>,
    },
    {
      title: 'Cost',
      dataIndex: 'cost_score',
      key: 'cost_score',
      width: 80,
      render: (score) => <Text style={{ color: getScoreColor(score) }}>{score.toFixed(0)}</Text>,
    },
    {
      title: 'Response',
      dataIndex: 'responsiveness_score',
      key: 'responsiveness_score',
      width: 80,
      render: (score) => <Text style={{ color: getScoreColor(score) }}>{score.toFixed(0)}</Text>,
    },
    {
      title: 'Risk',
      dataIndex: 'risk_score',
      key: 'risk_score',
      width: 80,
      render: (score) => (
        <Tooltip title="Higher = More Risk">
          <Tag color={getRiskColor(score)}>{score.toFixed(0)}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Trend',
      dataIndex: 'trend',
      key: 'trend',
      width: 60,
      render: (trend) => getTrendIcon(trend),
    },
  ];

  // Radar chart options
  const radarOptions = selectedVendor ? {
    title: {
      text: `${selectedVendor.vendor_name} Performance`,
      left: 'center',
      textStyle: { fontSize: 14 }
    },
    radar: {
      indicator: [
        { name: `Quality (${weights.quality_weight}%)`, max: 100 },
        { name: `Delivery (${weights.delivery_weight}%)`, max: 100 },
        { name: `Cost (${weights.cost_weight}%)`, max: 100 },
        { name: `Response (${weights.responsiveness_weight}%)`, max: 100 },
      ],
      radius: '65%'
    },
    series: [{
      type: 'radar',
      data: [{
        value: [
          selectedVendor.quality_score,
          selectedVendor.delivery_score,
          selectedVendor.cost_score,
          selectedVendor.responsiveness_score
        ],
        name: selectedVendor.vendor_name,
        areaStyle: { opacity: 0.3 }
      }]
    }]
  } : null;

  // Trend chart options
  const trendOptions = trendData.length > 0 ? {
    title: {
      text: 'Score Trend',
      left: 'center',
      textStyle: { fontSize: 14 }
    },
    tooltip: {
      trigger: 'axis'
    },
    legend: {
      data: ['Overall', 'Quality', 'Delivery', 'Cost', 'Responsiveness'],
      bottom: 0
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: trendData.map(d => `${d.year}-${String(d.month).padStart(2, '0')}`)
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100
    },
    series: [
      {
        name: 'Overall',
        type: 'line',
        data: trendData.map(d => d.overall_score),
        lineStyle: { width: 3 }
      },
      {
        name: 'Quality',
        type: 'line',
        data: trendData.map(d => d.quality_score),
        lineStyle: { type: 'dashed' }
      },
      {
        name: 'Delivery',
        type: 'line',
        data: trendData.map(d => d.delivery_score),
        lineStyle: { type: 'dashed' }
      },
      {
        name: 'Cost',
        type: 'line',
        data: trendData.map(d => d.cost_score),
        lineStyle: { type: 'dashed' }
      },
      {
        name: 'Responsiveness',
        type: 'line',
        data: trendData.map(d => d.responsiveness_score),
        lineStyle: { type: 'dashed' }
      }
    ]
  } : null;

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = [];
  for (let y = currentDate.getFullYear(); y >= currentDate.getFullYear() - 2; y--) {
    years.push({ value: y, label: y.toString() });
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <TrophyOutlined style={{ marginRight: 8 }} />
            Vendor Scorecards
          </Title>
        </Col>
        <Col>
          <Space>
            <Select
              value={selectedMonth}
              onChange={setSelectedMonth}
              options={months}
              style={{ width: 120 }}
            />
            <Select
              value={selectedYear}
              onChange={setSelectedYear}
              options={years}
              style={{ width: 100 }}
            />
            <Button icon={<ReloadOutlined />} onClick={loadData}>
              Refresh
            </Button>
            <Button icon={<SettingOutlined />} onClick={() => {
              weightsForm.setFieldsValue(weights);
              setWeightsModalVisible(true);
            }}>
              Weights
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateScore}>
              Add Score
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={24}>
        {/* Rankings Table */}
        <Col span={14}>
          <Card title="Vendor Rankings" bordered={false}>
            <Spin spinning={loading}>
              {rankings.length > 0 ? (
                <Table
                  dataSource={rankings}
                  columns={columns}
                  rowKey="client_vendor_id"
                  pagination={false}
                  size="middle"
                  onRow={(record) => ({
                    onClick: () => handleVendorSelect(record),
                    style: {
                      cursor: 'pointer',
                      background: selectedVendor?.client_vendor_id === record.client_vendor_id ? '#e6f7ff' : undefined
                    }
                  })}
                />
              ) : (
                <Empty description="No scorecards for this period">
                  <Button type="primary" onClick={handleCreateScore}>
                    Create First Scorecard
                  </Button>
                </Empty>
              )}
            </Spin>
          </Card>
        </Col>

        {/* Charts */}
        <Col span={10}>
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Card bordered={false}>
                {selectedVendor && radarOptions ? (
                  <ReactECharts option={radarOptions} style={{ height: 280 }} />
                ) : (
                  <Empty description="Select a vendor to view performance" style={{ padding: 60 }} />
                )}
              </Card>
            </Col>
            <Col span={24}>
              <Card bordered={false}>
                {trendData.length > 0 && trendOptions ? (
                  <ReactECharts option={trendOptions} style={{ height: 280 }} />
                ) : (
                  <Empty description="No trend data available" style={{ padding: 60 }} />
                )}
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Weights summary */}
      <Card style={{ marginTop: 16 }} size="small">
        <Row gutter={16} justify="center">
          <Col>
            <Statistic title="Quality Weight" value={`${weights.quality_weight}%`} valueStyle={{ fontSize: 16 }} />
          </Col>
          <Col>
            <Statistic title="Delivery Weight" value={`${weights.delivery_weight}%`} valueStyle={{ fontSize: 16 }} />
          </Col>
          <Col>
            <Statistic title="Cost Weight" value={`${weights.cost_weight}%`} valueStyle={{ fontSize: 16 }} />
          </Col>
          <Col>
            <Statistic title="Responsiveness Weight" value={`${weights.responsiveness_weight}%`} valueStyle={{ fontSize: 16 }} />
          </Col>
        </Row>
      </Card>

      {/* Add Score Modal */}
      <Modal
        title="Add Vendor Score"
        open={scoreModalVisible}
        onOk={handleSaveScore}
        onCancel={() => setScoreModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="client_vendor_id"
                label="Vendor"
                rules={[{ required: true, message: 'Select a vendor' }]}
              >
                <Select
                  placeholder="Select vendor"
                  showSearch
                  optionFilterProp="children"
                >
                  {vendors.map(v => (
                    <Select.Option key={v.id} value={v.id}>
                      {v.vendor?.name || `Vendor ${v.vendor_id}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="year" label="Year" rules={[{ required: true }]}>
                <Select options={years} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="month" label="Month" rules={[{ required: true }]}>
                <Select options={months} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="quality_score" label={`Quality Score (Weight: ${weights.quality_weight}%)`}>
            <Slider min={0} max={100} marks={{ 0: '0', 50: '50', 100: '100' }} />
          </Form.Item>

          <Form.Item name="delivery_score" label={`Delivery Score (Weight: ${weights.delivery_weight}%)`}>
            <Slider min={0} max={100} marks={{ 0: '0', 50: '50', 100: '100' }} />
          </Form.Item>

          <Form.Item name="cost_score" label={`Cost Score (Weight: ${weights.cost_weight}%)`}>
            <Slider min={0} max={100} marks={{ 0: '0', 50: '50', 100: '100' }} />
          </Form.Item>

          <Form.Item name="responsiveness_score" label={`Responsiveness Score (Weight: ${weights.responsiveness_weight}%)`}>
            <Slider min={0} max={100} marks={{ 0: '0', 50: '50', 100: '100' }} />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional notes about this score" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Weights Modal */}
      <Modal
        title="Configure Scoring Weights"
        open={weightsModalVisible}
        onOk={handleSaveWeights}
        onCancel={() => setWeightsModalVisible(false)}
      >
        <Form form={weightsForm} layout="vertical">
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            <InfoCircleOutlined /> Weights must sum to 100%
          </Text>

          <Form.Item name="quality_weight" label="Quality Weight (%)">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="delivery_weight" label="Delivery Weight (%)">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="cost_weight" label="Cost Weight (%)">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="responsiveness_weight" label="Responsiveness Weight (%)">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VendorScorecards;
